import React, { useMemo } from 'react'

import { Calendar } from 'lucide-react'
import { useSelector } from 'react-redux'
import { Link, useLocation } from 'react-router-dom'

import { usePermission } from '@/core/hooks/usePermission'
import { useTranslation } from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'
import { useGuideProgress } from '@/features/guide/hooks/useGuideProgress'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'

import Icon from '@shared/components/atoms/Icon'
import { LogoIcon, LogoIconInverse } from '@shared/components/atoms/Logo'

// import { toggleTheme as toggleUITheme } from '@/core/store/slices/ui'

interface NavItemProps {
	id: string
	icon: React.ReactNode
	to?: string
	label: string
	isActive?: boolean
	onClick?: () => void
	badge?: number
}

const NavItem: React.FC<NavItemProps> = ({
	id,
	icon,
	to,
	label,
	isActive,
	onClick = () => {},
	badge,
}) => {
	const content = (
		<div
			className="w-full flex justify-start lg:justify-center items-center py-1.5 2xl:py-2 cursor-pointer px-4 lg:px-0"
			onClick={onClick}
			id={id}
		>
			<div className="relative">
				<div
					className={`
						w-10 h-10 2xl:w-12 2xl:h-12 flex items-center justify-center rounded-lg 2xl:rounded-xl
						border transition-all duration-200 shrink-0
						[&>svg]:w-4 [&>svg]:h-4 2xl:[&>svg]:w-5 2xl:[&>svg]:h-5
						${
							isActive
								? 'bg-main-accent text-main-accent-foreground border-main-accent shadow-lg'
								: 'border-border text-muted-foreground hover:border-border hover:text-foreground'
						}
					`}
				>
					{icon}
				</div>
				{badge !== undefined && badge > 0 && (
					<span className="absolute -top-1.5 -right-1.5 min-w-5 h-5 flex items-center justify-center px-1 text-[10px] font-semibold bg-primary text-secondary rounded-full shadow-sm">
						{badge > 9 ? '9+' : badge}
					</span>
				)}
			</div>
			{label && (
				<span className="ml-3 lg:hidden text-sm font-medium text-foreground">
					{label}
				</span>
			)}
		</div>
	)

	if (to) {
		return (
			<Link to={to} title={label} className="w-full">
				{content}
			</Link>
		)
	}

	return content
}

export const NavbarContent: React.FC<{ onItemClick?: () => void }> = ({
	onItemClick,
}) => {
	const location = useLocation()
	const { t, changeLanguage, currentLanguage } = useTranslation('common')
	const account = useCurrentAccount()
	const { pendingRequiredCount, pendingOptionalCount } = useGuideProgress()
	const { features } = usePermission()

	const { theme } = useSelector((state: RootState) => state.ui)
	/* const toggleTheme = () => {
		store.dispatch(toggleUITheme())
	} */

	// Check if account has an active trial
	const hasActiveTrial = useMemo(() => {
		const trialSubscriptions = account?.getTrialSubscriptions() ?? []
		return trialSubscriptions.length > 0
	}, [account])

	const toggleLanguage = () => {
		const newLang = currentLanguage?.startsWith('es') ? 'en' : 'es'
		changeLanguage(newLang)
		if (onItemClick) onItemClick()
	}

	const handleItemClick = () => {
		if (onItemClick) onItemClick()
	}

	const upperItems = useMemo(() => {
		const items = [
			// Guide item - disabled for now
			/* ...(hasActiveTrial
				? [
						{
							id: 'nav-item-guide',
							icon: (
								<span className="[&>svg]:!w-6 [&>svg]:!h-6 2xl:[&>svg]:!w-7 2xl:[&>svg]:!h-7">
									<ListTodo
										strokeWidth={1.5}
										className="[&_*]:!stroke-[currentColor]"
									/>
								</span>
							),
							to: '/guide',
							label: t('navigation.guide'),
							badge: pendingRequiredCount + pendingOptionalCount,
						},
					]
				: []), */
			{
				id: 'nav-item-zones',
				icon: <Icon name="geo-alt" />,
				to: '/zones',
				label: t('navigation.zones'),
			},
			{
				id: 'nav-item-music',
				icon: <Icon name="music-note-beamed" />,
				to: '/music',
				label: t('navigation.music'),
			},
			{
				id: 'nav-item-schedules',
				icon: (
					<span className="[&>svg]:!w-6 [&>svg]:!h-6 2xl:[&>svg]:!w-7 2xl:[&>svg]:!h-7">
						<Calendar
							strokeWidth={1.5}
							className="[&_*]:!stroke-[currentColor]"
						/>
					</span>
				),
				to: '/schedules',
				label: t('navigation.schedule'),
			},
			{
				id: 'nav-item-campaigns',
				icon: <Icon name="megaphone" />,
				to: '/campaigns',
				label: t('navigation.campaigns'),
			},
			{
				id: 'nav-item-account',
				icon: <Icon name="person" />,
				to: '/account',
				label: t('navigation.account'),
			},
			...(features.integrations.enabled
				? [
						{
							id: 'nav-item-integrations',
							icon: <Icon name="boxes" />,
							to: '/integrations',
							label: t('navigation.integrations'),
						},
					]
				: []),
		]
		return items
	}, [
		hasActiveTrial,
		t,
		pendingRequiredCount,
		pendingOptionalCount,
		features.integrations.enabled,
	])

	const lowerItems = [
		{
			id: 'nav-item-download-app',
			icon: <Icon name="cloud-download" />,
			label: t('navigation.download_app'),
			onClick: () => {
				const downloadUrl = import.meta.env.VITE_DOWNLOAD_APP_URL as
					| string
					| undefined
				if (downloadUrl) {
					window.open(downloadUrl, '_blank', 'noopener,noreferrer')
				}
				if (onItemClick) onItemClick()
			},
		},
		{
			id: 'nav-item-language',
			icon: <Icon name="globe2" />,
			label: t('navigation.language'),
			onClick: toggleLanguage,
		},
		/* {
			icon: <Icon name={theme === 'light' ? 'moon-fill' : 'sun-fill'} />,
			label: t('navigation.theme'),
			onClick: toggleTheme,
			navLabel: 'Theme',
		}, */
	]

	return (
		<>
			{/* Logo */}
			<div className="flex lg:items-center lg:justify-center w-full max-w-unset px-6 py-4 2xl:py-4 [&>svg]:w-7 [&>svg]:h-7 2xl:[&>svg]:w-8 2xl:[&>svg]:h-8">
				{theme === 'light' ? (
					<LogoIconInverse width="28px" height="28px" />
				) : (
					<LogoIcon width="28px" height="28px" />
				)}
			</div>

			{/* Navigation Items */}
			<div className="flex flex-col justify-between w-full flex-1 overflow-y-auto">
				<div className="flex flex-col items-center w-full py-1.5 2xl:py-2">
					{upperItems.map((item, index) => (
						<NavItem
							key={index}
							icon={item.icon}
							to={item.to}
							label={item.label}
							isActive={location.pathname.startsWith(item.to || '')}
							onClick={handleItemClick}
							id={item.id}
							badge={
								'badge' in item ? (item.badge as number | undefined) : undefined
							}
						/>
					))}
				</div>

				<div className="flex flex-col items-center w-full py-1.5 2xl:py-2">
					{lowerItems.map((item, index) => (
						<NavItem
							id={item.id}
							key={index}
							icon={item.icon}
							label={item.label}
							isActive={false}
							onClick={item.onClick}
						/>
					))}
				</div>
			</div>
		</>
	)
}

const Navbar: React.FC = () => {
	return (
		<nav className="hidden lg:flex flex-col items-center lg:w-16 2xl:w-[72px] lg:h-[calc(100vh-24px)] 2xl:h-[calc(100vh-32px)] bg-card fixed top-3 left-3 2xl:top-4 2xl:left-4 z-50 border border-border rounded-lg 2xl:rounded-xl">
			<NavbarContent />
		</nav>
	)
}

export default Navbar
